import { useEffect } from "react";
import { useGlobalState } from "@/helpers/useGlobalState";
import { getGlobalContentitem } from "@/helpers/contentHelpers";
import { CookiePreferencesLink } from "@/components/CookieConsent";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import type { ImageProps } from "@/types/imageProps";
import { withLocale } from "@/helpers/withLocale";
import { topMenuItems, bottomMenuItems, renderMenuLabel } from "@/helpers/menuHelpers";
import MalencoNaw from "@/components/MalencoNaw";
import { useMsal } from "@azure/msal-react";
import { getActiveAccount, logout } from "@/auth/auth";
import { loginRequest } from "@/auth/msalConfig";
import { useIsAdmin } from "@/helpers/useIsAdmin";
import { useIsCustomerManager } from "@/helpers/useIsCustomerManager";
import { applicationApi } from "@/api/applicationApi";

type LayoutProps = { titleCode?: string };

export default function Layout({ titleCode }: LayoutProps) {
    const { globalState, updateGlobalState } = useGlobalState();
    const { instance } = useMsal();

    const activeAccount = getActiveAccount(instance);
    const isAuthenticated = !!activeAccount;
    const isAdmin = useIsAdmin();
    const isCustomerManager = useIsCustomerManager();

    const pageTitle =
        titleCode
            ? getGlobalContentitem(titleCode, globalState)?.title
            : null;

    const logo: ImageProps = {
        url: "Malenco_Academy_schild.png",
        alt: getGlobalContentitem("MalencoLogoImg", globalState).title,
        title: getGlobalContentitem("MalencoLogoImg", globalState).content
    };

    const navigate = useNavigate();
    const location = useLocation();

    const handleLanguageChange = (newLanguage: string) => {
        if (newLanguage === globalState.Language) return;

        // Construct new URL with language prefix
        const currentPath = location.pathname;
        const newPath = withLocale(currentPath.replace(/^\/nl\/|^\/en\//, "/"), newLanguage);
        navigate(newPath);
    };

    useEffect(() => {
        try {
            const title = pageTitle ? `${pageTitle} | Malenco Academy` : "Malenco Academy";
            const description = getGlobalContentitem("OpenGraphPageDescription", globalState)?.content;
            const url = window.location.href;
            const image = `${window.location.origin}/media/img/` + logo.url;

            document.title = title;

            const setMeta = (property: string, content: string) => {
                let tag = document.querySelector(`meta[property='${property}']`) as HTMLMetaElement;
                if (!tag) {
                    tag = document.createElement("meta");
                    tag.setAttribute("property", property);
                    document.head.appendChild(tag);
                }
                tag.setAttribute("content", content);
            };

            setMeta("og:title", title);
            setMeta("og:description", description ?? "");
            setMeta("og:type", "website");
            setMeta("og:url", url);
            setMeta("og:image", image);
        } catch (err) {
            console.error("Error in LayoutNoHeader useEffect:", err);
        }
    }, [globalState.Logo?.url, globalState.Contentitems]);

    return (
        <div className="container">
            <div className="left"></div>
            <div className="center">
                <div className="PagebarSection">
                    <div className="PagebarLogoSection">
                        <img
                            src={"/media/img/" + logo.url}
                            alt={logo.alt}
                            title={logo.title}
                        />
                    </div>
                    <div className="PagebarNameSection">Malenco Academy</div>
                    <div className="PagebarTitleSection">
                        <h1>{pageTitle}</h1>
                    </div>
                    <header className={"TopMenuBar AcademyHeaderBg"}>
                        <nav className="TopMenuBarInner">
                            {topMenuItems
                                .filter(item => {
                                    // Filter out admin links if user is not admin
                                    if (item.to === "/admin/programs") {
                                        return isAuthenticated && isAdmin;
                                    }
                                    // Filter out manager links if user is not CustomerManager
                                    if (item.to.startsWith("/manager")) {
                                        return isAuthenticated && isCustomerManager;
                                    }
                                    return true;
                                })
                                .map((item) => (
                                    <Link key={item.to} to={withLocale(item.to, globalState.Language)} className={"TopMenuLink" + (location.pathname === item.to ? " Active" : "")} title={getGlobalContentitem(item.code, globalState).content}>
                                        {renderMenuLabel(item.code, item.fallback, globalState)}
                                    </Link>
                                ))
                            }
                            {isAuthenticated ? (
                                <a
                                    onClick={(e) => {
                                        e.preventDefault();
                                        logout(instance);
                                    }}
                                    href="#"
                                    className="TopMenuLink"
                                    style={{ cursor: "pointer" }}
                                    title={getGlobalContentitem("LogoutBtn", globalState)?.content ?? "Afmelden"}
                                >
                                    {getGlobalContentitem("LogoutBtn", globalState)?.title ?? "Afmelden"}
                                </a>
                            ) : (
                                <a
                                    onClick={(e) => {
                                        e.preventDefault();
                                        instance.loginRedirect(loginRequest);
                                    }}
                                    href="#"
                                    className="TopMenuLink"
                                    style={{ cursor: "pointer" }}
                                    title={getGlobalContentitem("LoginBtn", globalState)?.content ?? "Inloggen"}
                                >
                                    {getGlobalContentitem("LoginBtn", globalState)?.title ?? "Inloggen"}
                                </a>
                            )}

                            {/* Language Switcher */}
                            <div className="TopMenuLink LanguageSwitcher">
                                <button
                                    onClick={() => handleLanguageChange("nl-NL")}
                                    className={globalState.Language === "nl-NL" ? "active" : ""}
                                    title="Nederlands"
                                >
                                    NL
                                </button>
                                <span>|</span>
                                <button
                                    onClick={() => handleLanguageChange("en-GB")}
                                    className={globalState.Language === "en-GB" ? "active" : ""}
                                    title="English"
                                >
                                    EN
                                </button>
                            </div>

                            {/* LinkedIn Icon */}
                            <a
                                href="https://www.linkedin.com/company/malenco-automatisering"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="TopMenuLink"
                                title={globalState.Language === "nl-NL" ? "Volg ons op LinkedIn" : "Follow us on LinkedIn"}
                                style={{ display: "flex", alignItems: "center", padding: "0 8px" }}
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                                </svg>
                            </a>
                        </nav>
                    </header>
                </div>

                <div>
                    <Outlet />
                </div>

                <div className="PagebarSection">
                    <div className="PagebarContentSection">
                        <MalencoNaw />
                    </div>
                    <div className="PagebarLinkSection">
                        {bottomMenuItems.map((item) => (
                            <Link key={item.to} to={withLocale(item.to, globalState.Language)}>{renderMenuLabel(item.code, item.fallback, globalState)}</Link>
                        ))}
                        <CookiePreferencesLink />
                    </div>
                </div>
            </div>
            <div className="right"></div>
        </div>
    );
}
